import { useCallback, useState } from 'react';
import type { EmailTemplateArea } from '@immo/api-contract';

export type MailAddress = { readonly email: string; readonly name?: string };

export type OutboundAttachment = {
  readonly filename: string;
  readonly contentType: string;
  readonly contentBase64: string;
};

export type ComposerMode = 'new' | 'reply' | 'reply-all' | 'forward';

// The records a new e-mail is about (IMM-1177.4/.5). Set only by „E-Mail schreiben“;
// absent from the Postfach, the Kontakt and every reply / forward.
export type ComposerTemplateContext = {
  readonly contactId: string;
  readonly propertyId: string;
  readonly prospectId: string | null;
};

// The reply/forward context the host hands the composer. Everything is optional
// so a "new" composer can be opened with an empty seed.
export type ComposerSeed = {
  readonly subject?: string | null;
  readonly quotedHtml?: string;
  readonly to?: ReadonlyArray<MailAddress>;
  readonly cc?: ReadonlyArray<MailAddress>;
  readonly replyToProviderMessageId?: string;
  readonly signatureHtml?: string | null;
  readonly forwardAttachments?: ReadonlyArray<OutboundAttachment>;
  readonly templateContext?: ComposerTemplateContext;
  // The editable body the composer opens with, in composer markup (open-field chips allowed).
  readonly bodyHtml?: string;
  // Narrows „Vorlage einfügen“ to one Bereich when the recipient's role is known; absent means unknown.
  readonly templateArea?: EmailTemplateArea;
};

export type ComposerDescriptor = {
  readonly id: string;
  readonly mode: ComposerMode;
  readonly seed: ComposerSeed;
  readonly minimised: boolean;
};

export type UseComposers = {
  readonly composers: ReadonlyArray<ComposerDescriptor>;
  readonly openComposer: (mode: ComposerMode, seed: ComposerSeed) => string;
  readonly closeComposer: (id: string) => void;
  readonly toggleMinimise: (id: string) => void;
};

function nextComposerId(): string {
  // A stable-enough unique id — the composer only needs it as a React key and to
  // address close/minimise. crypto.randomUUID exists in jsdom and every target
  // browser; the fallback keeps this total if it is ever absent.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `composer-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Tracks WHICH composers exist and their minimised flag only. Each composer's
// form state lives inside its own mounted <EmailComposer/> — minimising merely
// flips a flag, it never unmounts, so typed content survives minimise/restore.
export function useComposers(): UseComposers {
  const [composers, setComposers] = useState<ReadonlyArray<ComposerDescriptor>>([]);

  const openComposer = useCallback((mode: ComposerMode, seed: ComposerSeed): string => {
    const id = nextComposerId();

    setComposers((prev) => [...prev, { id, mode, seed, minimised: false }]);

    return id;
  }, []);

  const closeComposer = useCallback((id: string) => {
    setComposers((prev) => prev.filter((composer) => composer.id !== id));
  }, []);

  const toggleMinimise = useCallback((id: string) => {
    setComposers((prev) =>
      prev.map((composer) => (composer.id === id ? { ...composer, minimised: !composer.minimised } : composer)),
    );
  }, []);

  return { composers, openComposer, closeComposer, toggleMinimise };
}
